/**
 * Board courses — how Wall&CeilingBoard is actually laid when something sits
 * on top of it.
 *
 * Board goes on in COURSES: 16" rows when it runs horizontally, 16" strips when
 * it runs vertically. A SlatWall inset (and later a window or a door) does not
 * restart those courses — the rows carry on at the same heights either side of
 * it, and the boards are simply cut where it is. So for a section with
 * something over it we lay the courses across the WHOLE section and then, for
 * each course, keep the stretches that still need board:
 *
 *   - a course the overlay covers completely, top to bottom, needs no board
 *     there at all — the course is interrupted, leaving a run on each side;
 *   - a course the overlay only bites into still needs its board in one piece;
 *     the board is notched to fit.
 *
 * The result is a list of RUNS: one length of board in one course. Both the
 * drawing and the material count come from the same runs, so what you see is
 * what is counted. (Counting the leftover frame as four separate rectangles —
 * the previous approach — rounds every rectangle's rows up on its own: a
 * 20' x 10' wall came to eight 20' boards with or without an 8' x 3' inset.)
 */

import { PanelOrientation } from '../../types';
import { PANEL_WIDTH_FEET } from '../constants';
import { Rect, intersectRect, subtractRects } from './resolver';
import { Outline, outlineSpansAbove, outlineTopRange } from './outline';

/** Anything shorter than this is not a piece of board (1/16"). */
const MIN_RUN_FT = 1 / 192;
const EPS = 1e-6;

export interface CourseRun {
  /** Which course this run is in, counted from the section's bottom (rows) or left (strips). */
  course: number;
  /**
   * The run's footprint: the full depth of its course by the length of the run.
   * Where an overlay bites into the course this still covers the bitten part —
   * subtract the overlays to get the visible shape (see `boardPieces`).
   */
  rect: Rect;
  /** Length of board along the run, in feet. */
  length: number;
  /** Depth of the course here — 16", or less for the last, cut course. */
  depth: number;
}

/** The overlays that actually touch `fill`. */
export function holesOver(fill: Rect, holes: Rect[]): Rect[] {
  return holes.filter((h) => intersectRect(fill, h));
}

/**
 * The part of a fill that still needs board, as a single rectangle: the fill
 * with any band an overlay covers edge-to-edge trimmed off its sides. Courses
 * are laid from THIS rectangle, so a SlatWall band dragged up over the bottom
 * of a section makes the rows start at the band's top — where an installer
 * would start them — instead of leaving a sliver course underneath it.
 * Null when nothing is left.
 */
export function trimToUncovered(fill: Rect, holes: Rect[]): Rect | null {
  const remaining = subtractRects(fill, holes);
  if (remaining.length === 0) return null;
  const x0 = Math.min(...remaining.map((r) => r.x));
  const y0 = Math.min(...remaining.map((r) => r.y));
  const x1 = Math.max(...remaining.map((r) => r.x + r.width));
  const y1 = Math.max(...remaining.map((r) => r.y + r.height));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/** Is every point of `cell` under an overlay? */
function fullyCovered(cell: Rect, holes: Rect[]): boolean {
  return subtractRects(cell, holes).length === 0;
}

/**
 * Lay courses across `fill` and return the runs of board they need, given the
 * overlays in `holes`. Works in surface coordinates (feet, origin bottom-left).
 *
 * With an `outline` (a wall under a sloped ceiling — see outline.ts) the
 * courses are cut to it the way an installer cuts them to the rake: a row
 * needs board only where the wall is taller than the row's bottom, so the
 * rows get shorter toward a gable's peak and vanish above the eave at the
 * corners; a vertical strip reaches the highest point of the wall over its
 * own width. The run's `rect` is the course's full depth over that length —
 * the rake cut itself is the outline, applied when the board is drawn.
 */
export function layoutCourses(
  fill: Rect,
  orientation: PanelOrientation,
  holes: Rect[],
  /** Depth of one course: 16" for Wall&CeilingBoard (the default), 6" for SlatWall rows. */
  pitch: number = PANEL_WIDTH_FEET,
  outline: Outline | null = null,
): CourseRun[] {
  const over = holesOver(fill, holes);
  const area = over.length ? trimToUncovered(fill, over) : fill;
  if (!area) return [];
  if (outline) return clipRunsToOutline(layoutCourses(area, orientation, over, pitch), orientation, outline);

  // Work in (along, across): `along` is the direction the boards run.
  const horizontal = orientation !== 'Vertical';
  const along0 = horizontal ? area.x : area.y;
  const alongLen = horizontal ? area.width : area.height;
  const across0 = horizontal ? area.y : area.x;
  const acrossLen = horizontal ? area.height : area.width;
  const toRect = (a: number, aLen: number, c: number, cLen: number): Rect =>
    (horizontal ? { x: a, y: c, width: aLen, height: cLen } : { x: c, y: a, width: cLen, height: aLen });

  // Places along the boards where an overlay starts or stops.
  const cuts = new Set<number>([along0, along0 + alongLen]);
  for (const h of over) {
    const h0 = horizontal ? h.x : h.y;
    const h1 = h0 + (horizontal ? h.width : h.height);
    if (h0 > along0 + EPS && h0 < along0 + alongLen - EPS) cuts.add(h0);
    if (h1 > along0 + EPS && h1 < along0 + alongLen - EPS) cuts.add(h1);
  }
  const stops = [...cuts].sort((a, b) => a - b);

  const runs: CourseRun[] = [];
  const courses = Math.ceil((acrossLen - EPS) / pitch);
  for (let course = 0; course < courses; course++) {
    const c0 = across0 + course * pitch;
    const depth = Math.min(pitch, across0 + acrossLen - c0);
    if (depth <= EPS) continue;

    let runStart: number | null = null;
    const close = (end: number) => {
      if (runStart === null) return;
      const length = end - runStart;
      if (length >= MIN_RUN_FT) runs.push({ course, rect: toRect(runStart, length, c0, depth), length, depth });
      runStart = null;
    };
    for (let i = 0; i < stops.length - 1; i++) {
      const a = stops[i];
      const b = stops[i + 1];
      if (fullyCovered(toRect(a, b - a, c0, depth), over)) close(a);
      else if (runStart === null) runStart = a;
    }
    close(along0 + alongLen);
  }
  return runs;
}

/** Cut runs laid over the rectangle down to the wall's real shape (see `layoutCourses`). */
function clipRunsToOutline(runs: CourseRun[], orientation: PanelOrientation, outline: Outline): CourseRun[] {
  const out: CourseRun[] = [];
  if (orientation !== 'Vertical') {
    for (const run of runs) {
      // A row needs board wherever the wall rises above the row's bottom.
      for (const [a, b] of outlineSpansAbove(outline, run.rect.y, run.rect.x, run.rect.x + run.rect.width)) {
        if (b - a < MIN_RUN_FT) continue;
        out.push({ ...run, rect: { ...run.rect, x: a, width: b - a }, length: b - a });
      }
    }
    return out;
  }
  for (const run of runs) {
    // A strip reaches the highest point of the wall over its own width.
    const top = Math.min(run.rect.y + run.rect.height, outlineTopRange(outline, run.rect.x, run.rect.x + run.rect.width).max);
    const length = top - run.rect.y;
    if (length < MIN_RUN_FT) continue;
    out.push({ ...run, rect: { ...run.rect, height: length }, length });
  }
  return out;
}

/**
 * The visible shape of one board: its rectangle with the overlays cut out, as
 * pieces sliced ALONG the board. A row board notched by an inset comes back as
 * left end (full depth) / the strip under or over the inset / right end (full
 * depth) — each piece is a plain rectangle with one depth, which is what the
 * drawing needs to cut the profile back correctly over the notch.
 */
export function boardPieces(board: Rect, holes: Rect[], orientation: PanelOrientation): Rect[] {
  const over = holesOver(board, holes);
  if (over.length === 0) return [board];

  const horizontal = orientation !== 'Vertical';
  const along0 = horizontal ? board.x : board.y;
  const alongLen = horizontal ? board.width : board.height;
  const across0 = horizontal ? board.y : board.x;
  const acrossLen = horizontal ? board.height : board.width;
  const toRect = (a: number, aLen: number, c: number, cLen: number): Rect =>
    (horizontal ? { x: a, y: c, width: aLen, height: cLen } : { x: c, y: a, width: cLen, height: aLen });
  const acrossOf = (r: Rect) => (horizontal ? [r.y, r.height] : [r.x, r.width]);

  const cuts = new Set<number>([along0, along0 + alongLen]);
  for (const h of over) {
    const h0 = horizontal ? h.x : h.y;
    const h1 = h0 + (horizontal ? h.width : h.height);
    if (h0 > along0 + EPS && h0 < along0 + alongLen - EPS) cuts.add(h0);
    if (h1 > along0 + EPS && h1 < along0 + alongLen - EPS) cuts.add(h1);
  }
  const stops = [...cuts].sort((a, b) => a - b);

  // Each slice keeps whatever depth the overlays leave it; neighbouring slices
  // left with the same depth are one piece.
  const pieces: { a: number; len: number; c: number; cLen: number }[] = [];
  let open: typeof pieces = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const len = stops[i + 1] - a;
    const left = subtractRects(toRect(a, len, across0, acrossLen), over).map(acrossOf).sort((p, q) => p[0] - q[0]);
    const same = left.length === open.length
      && left.every(([c, cLen], k) => Math.abs(open[k].c - c) < EPS && Math.abs(open[k].cLen - cLen) < EPS);
    if (same) {
      open.forEach((p) => { p.len += len; });
    } else {
      open = left.map(([c, cLen]) => ({ a, len, c, cLen }));
      pieces.push(...open);
    }
  }
  return pieces.filter((p) => p.len >= MIN_RUN_FT && p.cLen >= MIN_RUN_FT).map((p) => toRect(p.a, p.len, p.c, p.cLen));
}

/**
 * Runs gathered by length, for counting: each group is "this many courses of
 * this length", which is exactly what the panel maths takes as a width and a
 * height. `depth` is the groups' combined course depth.
 */
export interface RunGroup {
  length: number;
  courses: number;
  depth: number;
}

export function groupRuns(runs: CourseRun[]): RunGroup[] {
  const groups = new Map<number, RunGroup>();
  for (const run of runs) {
    const key = Math.round(run.length * 1000) / 1000;
    const g = groups.get(key);
    if (g) { g.courses += 1; g.depth += run.depth; }
    else groups.set(key, { length: run.length, courses: 1, depth: run.depth });
  }
  return [...groups.values()].sort((a, b) => b.length - a.length);
}

/**
 * What is left of a SlatWall section once windows and doors are cut out of it,
 * as rectangles of WHOLE rows. A row an opening only bites into is not here —
 * see `partRowPieces` for the strip it leaves. (The count treats such a row as
 * whole: `layoutCourses` still counts it.)
 */
export function wholeRowPieces(rect: Rect, holes: Rect[], rowHeight: number): Rect[] {
  const over = holesOver(rect, holes);
  if (over.length === 0) return [rect];

  const cuts = new Set<number>([rect.x, rect.x + rect.width]);
  for (const h of over) {
    if (h.x > rect.x + EPS && h.x < rect.x + rect.width - EPS) cuts.add(h.x);
    const h1 = h.x + h.width;
    if (h1 > rect.x + EPS && h1 < rect.x + rect.width - EPS) cuts.add(h1);
  }
  const stops = [...cuts].sort((a, b) => a - b);

  const pieces: Rect[] = [];
  let open: Rect[] = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const x = stops[i];
    const width = stops[i + 1] - x;
    const kept = subtractRects({ x, y: rect.y, width, height: rect.height }, over)
      .map((r) => {
        // Pull each end in to the nearest row line.
        const y0 = rect.y + Math.ceil((r.y - rect.y) / rowHeight - 1e-6) * rowHeight;
        const y1 = rect.y + Math.floor((r.y + r.height - rect.y) / rowHeight + 1e-6) * rowHeight;
        return { y: y0, height: y1 - y0 };
      })
      .filter((r) => r.height >= rowHeight - EPS)
      .sort((a, b) => a.y - b.y);
    const same = kept.length === open.length
      && kept.every((k, n) => Math.abs(open[n].y - k.y) < EPS && Math.abs(open[n].height - k.height) < EPS);
    if (same) {
      open.forEach((p) => { p.width += width; });
    } else {
      open = kept.map((k) => ({ x, y: k.y, width, height: k.height }));
      pieces.push(...open);
    }
  }
  return pieces.filter((p) => p.width >= MIN_RUN_FT);
}

/** A strip of a SlatWall row left beside an opening: less than a row high. */
export interface PartRowPiece extends Rect {
  /** Where the row it is cut from starts (the 6" grid line at or below it). */
  rowY: number;
}

/**
 * The strips `wholeRowPieces` leaves out: where an opening's top or bottom
 * falls part-way up a row, the rest of that row over (or under) the opening. A
 * door 80" tall in a band of 6" rows leaves 4" of a row above it; without this
 * the wall shows through there.
 */
export function partRowPieces(rect: Rect, holes: Rect[], rowHeight: number): PartRowPiece[] {
  const over = holesOver(rect, holes);
  if (over.length === 0) return [];

  const cuts = new Set<number>([rect.x, rect.x + rect.width]);
  for (const h of over) {
    if (h.x > rect.x + EPS && h.x < rect.x + rect.width - EPS) cuts.add(h.x);
    const h1 = h.x + h.width;
    if (h1 > rect.x + EPS && h1 < rect.x + rect.width - EPS) cuts.add(h1);
  }
  const stops = [...cuts].sort((a, b) => a - b);

  const pieces: PartRowPiece[] = [];
  let open: PartRowPiece[] = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const x = stops[i];
    const width = stops[i + 1] - x;
    const kept: { y: number; height: number; rowY: number }[] = [];
    for (const r of subtractRects({ x, y: rect.y, width, height: rect.height }, over)) {
      // Walk the rows this stretch touches; keep the ones it only partly fills.
      const first = Math.floor((r.y - rect.y) / rowHeight + 1e-6);
      const last = Math.ceil((r.y + r.height - rect.y) / rowHeight - 1e-6);
      for (let row = first; row < last; row++) {
        const rowY = rect.y + row * rowHeight;
        const y0 = Math.max(r.y, rowY);
        const y1 = Math.min(r.y + r.height, rowY + rowHeight);
        if (y1 - y0 > MIN_RUN_FT && y1 - y0 < rowHeight - EPS) kept.push({ y: y0, height: y1 - y0, rowY });
      }
    }
    kept.sort((a, b) => a.y - b.y);
    const same = kept.length === open.length
      && kept.every((k, n) => Math.abs(open[n].y - k.y) < EPS && Math.abs(open[n].height - k.height) < EPS);
    if (same) {
      open.forEach((p) => { p.width += width; });
    } else {
      open = kept.map((k) => ({ x, width, ...k }));
      pieces.push(...open);
    }
  }
  return pieces;
}
